import {
  collection, doc, getDocs, getDoc, addDoc, setDoc, updateDoc,
  deleteDoc, query, orderBy, limit, where, onSnapshot,
  serverTimestamp, writeBatch, increment
} from 'firebase/firestore';
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject
} from 'firebase/storage';
import { db, storage } from './config';

// ── PLATFORM PROFIT RATE ─────────────────────────────────────────────────────
export const PROFIT_RATE = 0.10; // 10% of every transaction

/** Normalize Firestore Timestamp / ISO string / epoch to Date. */
export function toJsDate(val) {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val.seconds != null) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

function monthsForRange(range) {
  if (range === '3m') return 3;
  if (range === '12m') return 12;
  return 6;
}

// ── Image Upload ──────────────────────────────────────────────────────────────
export async function uploadImage(file, path, onProgress) {
  const storageRef = ref(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      snap => onProgress && onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      reject,
      async () => { resolve(await getDownloadURL(task.snapshot.ref)); }
    );
  });
}

export async function deleteImage(url) {
  try { await deleteObject(ref(storage, url)); } catch (_) {}
}

// ── Flights ───────────────────────────────────────────────────────────────────
export const flightsCol = () => collection(db, 'flights');

export async function getFlights() {
  const snap = await getDocs(query(flightsCol(), orderBy('airline')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addFlight(data) {
  return addDoc(flightsCol(), { ...data, createdAt: serverTimestamp() });
}

export async function updateFlight(id, data) {
  return updateDoc(doc(db, 'flights', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteFlight(id) {
  return deleteDoc(doc(db, 'flights', id));
}

// ── Events ────────────────────────────────────────────────────────────────────
export const eventsCol = () => collection(db, 'events');

export async function getEvents() {
  const snap = await getDocs(query(eventsCol(), orderBy('date')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addEvent(data, imageFile, onProgress) {
  let imageUrl = data.imageUrl || '';
  if (imageFile) {
    imageUrl = await uploadImage(imageFile, `events/${Date.now()}_${imageFile.name}`, onProgress);
  }
  return addDoc(eventsCol(), { ...data, imageUrl, createdAt: serverTimestamp() });
}

export async function updateEvent(id, data, imageFile, onProgress) {
  let imageUrl = data.imageUrl || '';
  if (imageFile) {
    if (imageUrl) await deleteImage(imageUrl);
    imageUrl = await uploadImage(imageFile, `events/${Date.now()}_${imageFile.name}`, onProgress);
  }
  return updateDoc(doc(db, 'events', id), { ...data, imageUrl, updatedAt: serverTimestamp() });
}

export async function deleteEvent(id, imageUrl) {
  if (imageUrl) await deleteImage(imageUrl);
  return deleteDoc(doc(db, 'events', id));
}

// ── Featured Destinations ─────────────────────────────────────────────────────
export const destinationsCol = () => collection(db, 'destinations');

export async function getDestinations() {
  const snap = await getDocs(query(destinationsCol(), orderBy('order')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addDestination(data, imageFile, onProgress) {
  let imageUrl = data.imageUrl || '';
  if (imageFile) {
    imageUrl = await uploadImage(imageFile, `destinations/${Date.now()}_${imageFile.name}`, onProgress);
  }
  return addDoc(destinationsCol(), { ...data, imageUrl, createdAt: serverTimestamp() });
}

export async function updateDestination(id, data, imageFile, onProgress) {
  let imageUrl = data.imageUrl || '';
  if (imageFile) {
    if (imageUrl) await deleteImage(imageUrl);
    imageUrl = await uploadImage(imageFile, `destinations/${Date.now()}_${imageFile.name}`, onProgress);
  }
  return updateDoc(doc(db, 'destinations', id), { ...data, imageUrl, updatedAt: serverTimestamp() });
}

export async function deleteDestination(id, imageUrl) {
  if (imageUrl) await deleteImage(imageUrl);
  return deleteDoc(doc(db, 'destinations', id));
}

// ── Users ─────────────────────────────────────────────────────────────────────
const usersCol = () => collection(db, 'users');

export async function getUsers() {
  try {
    const snap = await getDocs(query(usersCol(), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch {
    const snap = await getDocs(usersCol());
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (toJsDate(b.createdAt)?.getTime() || 0) - (toJsDate(a.createdAt)?.getTime() || 0));
  }
}

export async function getUser(id) {
  const snap = await getDoc(doc(db, 'users', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/** Create user profile (Firestore). Auth account must exist separately or use app register. */
export async function addUser(data) {
  const payload = {
    fullName: data.fullName || '',
    email: data.email || '',
    phone: data.phone || '',
    country: data.country || '',
    countryCode: data.countryCode || '',
    photoUrl: data.photoUrl || '',
    verified: !!data.verified,
    blocked: !!data.blocked,
    role: data.role || 'user',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (data.uid?.trim()) {
    const id = data.uid.trim();
    await setDoc(doc(db, 'users', id), { ...payload, uid: id });
    return id;
  }
  const ref = await addDoc(usersCol(), payload);
  return ref.id;
}

export async function updateUser(id, data) {
  const { id: _id, createdAt, ...rest } = data;
  return updateDoc(doc(db, 'users', id), {
    ...rest,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteUser(id) {
  const ticketsSnap = await getDocs(collection(db, 'users', id, 'tickets'));
  const batch = writeBatch(db);
  ticketsSnap.docs.forEach(t => batch.delete(t.ref));
  batch.delete(doc(db, 'users', id));
  await batch.commit();
}

// ── Bookings ──────────────────────────────────────────────────────────────────
const bookingsCol = () => collection(db, 'bookings');

export function generateBookingRef() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let ref = 'MM';
  for (let i = 0; i < 6; i++) ref += chars[Math.floor(Math.random() * chars.length)];
  return ref;
}

export async function getBookings() {
  try {
    const snap = await getDocs(query(bookingsCol(), orderBy('bookedAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch {
    const snap = await getDocs(bookingsCol());
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const ad = toJsDate(a.bookedAt)?.getTime() || 0;
        const bd = toJsDate(b.bookedAt)?.getTime() || 0;
        return bd - ad;
      });
  }
}

export async function addBooking(data) {
  const ref = (data.bookingRef || generateBookingRef()).toUpperCase();
  const payload = {
    bookingRef: ref,
    type: data.type || 'flight',
    status: data.status || 'confirmed',
    title: data.title || '',
    subtitle: data.subtitle || '',
    userId: data.userId || '',
    totalPrice: Number(data.totalPrice) || 0,
    bookedAt: data.bookedAt || new Date().toISOString(),
    paymentMethod: data.paymentMethod || 'admin',
    createdBy: 'admin',
    updatedAt: serverTimestamp(),
  };
  await setDoc(doc(db, 'bookings', ref), payload);
  return ref;
}

export async function updateBooking(id, data) {
  const payload = { ...data, updatedAt: serverTimestamp() };
  delete payload.id;
  delete payload.createdAt;
  if (payload.totalPrice != null) payload.totalPrice = Number(payload.totalPrice);
  return updateDoc(doc(db, 'bookings', id), payload);
}

export async function deleteBooking(id) {
  return deleteDoc(doc(db, 'bookings', id));
}

export async function cancelBooking(id) {
  return updateDoc(doc(db, 'bookings', id), {
    status: 'cancelled',
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function confirmBooking(id) {
  return updateDoc(doc(db, 'bookings', id), {
    status: 'confirmed',
    updatedAt: serverTimestamp(),
  });
}

// ── Analytics ─────────────────────────────────────────────────────────────────
async function fetchCoreStats() {
  const [bookings, users, flights, events, destinations] = await Promise.all([
    getDocs(collection(db, 'bookings')),
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'flights')),
    getDocs(collection(db, 'events')),
    getDocs(collection(db, 'destinations')),
  ]);

  const bookingData = bookings.docs.map(d => ({ id: d.id, ...d.data() }));
  const userData = users.docs.map(d => ({ id: d.id, ...d.data() }));

  return {
    bookingData,
    userData,
    totalUsers: users.size,
    totalFlights: flights.size,
    totalEvents: events.size,
    totalDestinations: destinations.size,
  };
}

export async function getAnalytics() {
  const { bookingData, totalUsers, totalFlights, totalEvents, totalDestinations } =
    await fetchCoreStats();

  const totalRevenue = bookingData.reduce((s, b) => s + (b.totalPrice || 0), 0);
  const platformProfit = totalRevenue * PROFIT_RATE;

  const now = new Date();
  const monthlyRevenue = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const label = d.toLocaleString('default', { month: 'short' });
    const revenue = bookingData
      .filter(b => {
        const bd = toJsDate(b.bookedAt);
        return bd && bd.getMonth() === d.getMonth() && bd.getFullYear() === d.getFullYear();
      })
      .reduce((s, b) => s + (b.totalPrice || 0), 0);
    return { month: label, revenue, profit: revenue * PROFIT_RATE };
  });

  const flightBookings = bookingData.filter(b => b.type === 'flight').length;
  const eventBookings = bookingData.filter(b => b.type === 'event').length;
  const confirmed = bookingData.filter(b => b.status === 'confirmed').length;
  const cancelled = bookingData.filter(b => b.status === 'cancelled').length;

  const recentBookings = [...bookingData]
    .sort((a, b) => (toJsDate(b.bookedAt)?.getTime() || 0) - (toJsDate(a.bookedAt)?.getTime() || 0))
    .slice(0, 5)
    .map(b => ({
      ...b,
      bookedAtLabel: toJsDate(b.bookedAt)?.toLocaleDateString() || '—',
    }));

  return {
    totalRevenue,
    platformProfit,
    totalBookings: bookingData.length,
    totalUsers,
    totalFlights,
    totalEvents,
    totalDestinations,
    flightBookings,
    eventBookings,
    confirmed,
    cancelled,
    monthlyRevenue,
    recentBookings,
    avgBookingValue: bookingData.length ? totalRevenue / bookingData.length : 0,
  };
}

export async function getReportingData(range = '6m') {
  const months = monthsForRange(range);
  const { bookingData, userData, totalUsers, totalFlights, totalEvents } =
    await fetchCoreStats();

  const totalRevenue = bookingData.reduce((s, b) => s + (b.totalPrice || 0), 0);
  const platformProfit = totalRevenue * PROFIT_RATE;
  const confirmed = bookingData.filter(b => b.status === 'confirmed').length;
  const cancelled = bookingData.filter(b => b.status === 'cancelled').length;
  const pending = bookingData.length - confirmed - cancelled;

  const now = new Date();
  const rangeStart = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);

  const inRange = d => d && d >= rangeStart;

  const newUsersInRange = userData.filter(u => inRange(toJsDate(u.createdAt))).length;

  const bookingsByMonth = Array.from({ length: months }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
    const label = d.toLocaleString('default', { month: 'short', year: '2-digit' });
    const monthBookings = bookingData.filter(b => {
      const bd = toJsDate(b.bookedAt);
      return bd && bd.getMonth() === d.getMonth() && bd.getFullYear() === d.getFullYear();
    });
    return {
      month: label,
      flights: monthBookings.filter(b => b.type === 'flight').length,
      events: monthBookings.filter(b => b.type === 'event').length,
    };
  });

  const userSignupsByMonth = Array.from({ length: months }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
    const label = d.toLocaleString('default', { month: 'short' });
    const count = userData.filter(u => {
      const cd = toJsDate(u.createdAt);
      return cd && cd.getMonth() === d.getMonth() && cd.getFullYear() === d.getFullYear();
    }).length;
    return { month: label, users: count };
  });

  const flightRevenue = bookingData
    .filter(b => b.type === 'flight')
    .reduce((s, b) => s + (b.totalPrice || 0), 0);
  const eventRevenue = bookingData
    .filter(b => b.type === 'event')
    .reduce((s, b) => s + (b.totalPrice || 0), 0);

  const revenueByType = [
    { name: 'Flights', value: flightRevenue },
    { name: 'Events', value: eventRevenue },
  ].filter(r => r.value > 0);

  const recentBookings = [...bookingData]
    .sort((a, b) => (toJsDate(b.bookedAt)?.getTime() || 0) - (toJsDate(a.bookedAt)?.getTime() || 0))
    .slice(0, 20)
    .map(b => ({
      ...b,
      bookedAtLabel: toJsDate(b.bookedAt)?.toLocaleDateString() || '—',
    }));

  return {
    totalRevenue,
    platformProfit,
    totalBookings: bookingData.length,
    totalUsers,
    catalogFlights: totalFlights,
    catalogEvents: totalEvents,
    confirmed,
    cancelled,
    pending,
    avgBookingValue: bookingData.length ? totalRevenue / bookingData.length : 0,
    newUsersInRange,
    bookingsByMonth,
    userSignupsByMonth,
    revenueByType: revenueByType.length ? revenueByType : [{ name: 'No revenue', value: 1 }],
    recentBookings,
  };
}
